/**
 * DamageClaimListPage — Karteritesi igenyek listaja
 * Epic 69: Garancia RMA & Cseregep
 *
 * Statusz szuro, jovahagyas/elutasitas muveletek.
 */

import type { DamageClaimStatusType } from '@/api/damage-claims';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  useApproveDamageClaim,
  useDamageClaims,
  useRejectDamageClaim,
} from '@/hooks/useDamageClaims';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STATUS_LABEL_KEYS: Record<string, string> = {
  PENDING: 'service.damageClaim.statusLabels.pending',
  APPROVED: 'service.damageClaim.statusLabels.approved',
  REJECTED: 'service.damageClaim.statusLabels.rejected',
  SETTLED: 'service.damageClaim.statusLabels.settled',
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  APPROVED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  REJECTED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  SETTLED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
};

const TYPE_LABEL_KEYS: Record<string, string> = {
  GAR: 'service.damageClaim.typeLabels.warranty',
  BH: 'service.damageClaim.typeLabels.damage',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function DamageClaimListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<DamageClaimStatusType | undefined>(undefined);
  const { claims, isLoading, error, refetch } = useDamageClaims(
    statusFilter !== undefined ? { status: statusFilter } : undefined
  );
  const approveMutation = useApproveDamageClaim();
  const rejectMutation = useRejectDamageClaim();

  const handleApprove = async (id: string) => {
    await approveMutation.mutateAsync(id);
    refetch();
  };

  const handleReject = async (id: string) => {
    const note = prompt(t('service.damageClaim.rejectionPrompt'));
    if (!note) return;
    await rejectMutation.mutateAsync({ id, rejectionNote: note });
    refetch();
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('service.damageClaim.title')}</h1>
        <div className="text-center py-8 text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('service.damageClaim.title')}</h1>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-red-500">{t('service.errorPrefix', { message: error })}</p>
            <Button className="mt-4" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('service.damageClaim.title')}</h1>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={statusFilter === undefined ? 'default' : 'outline'}
          size="sm"
          onClick={() => setStatusFilter(undefined)}
        >
          {t('common.all')}
        </Button>
        {(['PENDING', 'APPROVED', 'REJECTED', 'SETTLED'] as DamageClaimStatusType[]).map(s => (
          <Button
            key={s}
            variant={statusFilter === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(s)}
          >
            {STATUS_LABEL_KEYS[s] ? t(STATUS_LABEL_KEYS[s]) : s}
          </Button>
        ))}
      </div>

      {/* Claims list */}
      {claims.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            {t('service.damageClaim.noClaims')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {claims.map(claim => (
            <Card key={claim.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[claim.status] ?? ''}`}
                      >
                        {STATUS_LABEL_KEYS[claim.status]
                          ? t(STATUS_LABEL_KEYS[claim.status])
                          : claim.status}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {TYPE_LABEL_KEYS[claim.type] ? t(TYPE_LABEL_KEYS[claim.type]) : claim.type}
                      </span>
                      <span className="text-sm text-muted-foreground">#{claim.id.slice(0, 8)}</span>
                    </div>
                    <div className="flex gap-6 text-sm">
                      <span>
                        {t('service.damageClaim.repairCost', {
                          amount: formatCurrency(claim.repairCost),
                        })}
                      </span>
                      <span>
                        {t('service.damageClaim.lostRevenue', {
                          amount: formatCurrency(claim.lostRevenue),
                        })}
                      </span>
                      <span className="font-bold">
                        {t('service.damageClaim.totalClaim', {
                          amount: formatCurrency(claim.totalClaim),
                        })}
                      </span>
                    </div>
                    {claim.note && (
                      <p className="text-sm text-muted-foreground mt-1">{claim.note}</p>
                    )}
                    {claim.rejectionNote && (
                      <p className="text-sm text-red-500 mt-1">
                        {t('service.damageClaim.rejectedPrefix', { note: claim.rejectionNote })}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/worksheet/damage-claims/${claim.id}`)}
                    >
                      {t('common.details')}
                    </Button>
                    {claim.status === 'PENDING' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(claim.id)}
                          disabled={approveMutation.isPending}
                        >
                          {t('service.damageClaim.approve')}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleReject(claim.id)}
                          disabled={rejectMutation.isPending}
                        >
                          {t('service.damageClaim.reject')}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
